const BUNDLE_PARENT_PROPERTY = '_bundle_parent';
const BUNDLE_DISCOUNT_PROPERTY = '_bundle_discount';
const REMOVE_BUTTON_SELECTOR = '.kaching-cart-item[data-bundle-parent] .kaching-cart-item__remove-btn';

class KachingBundleLines {
  #root = null;
  #observer = null;
  #frame = 0;
  #pendingRemovals = new Map();
  #dialog = null;
  #pendingButton = null;
  #confirmedButton = null;

  init() {
    this.#dialog = document.getElementById('KachingBundleConfirm');
    this.#dialog?.addEventListener('close', this.#handleDialogClose);
    this.#dialog?.addEventListener('click', this.#handleDialogClick);
    this.#observer = new MutationObserver(this.#handleMutations);
    this.#attach();
  }

  #attach() {
    this.#root?.removeEventListener('click', this.#handleClick, true);
    this.#root = document.getElementById('kaching-cart');

    if (!this.#root) {
      this.#observer.observe(document.body, { childList: true });
      return;
    }

    this.#observer.disconnect();
    this.#observer.observe(this.#root, {
      childList: true,
      subtree: true,
      attributeFilter: ['data-line-key'],
    });
    this.#root.addEventListener('click', this.#handleClick, true);
    this.#scheduleSync();
  }

  #handleMutations = () => {
    if (!this.#root?.isConnected) {
      this.#attach();
      return;
    }

    this.#scheduleSync();
  };

  #handleClick = (event) => {
    const button = event.target.closest(REMOVE_BUTTON_SELECTOR);
    if (!button) return;

    if (this.#confirmedButton === button) {
      this.#confirmedButton = null;
      this.#trackRemoval(button);
      return;
    }

    if (!this.#dialog) {
      this.#trackRemoval(button);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#pendingButton = button;
    this.#dialog.returnValue = '';
    this.#dialog.showModal();
  };

  #handleDialogClick = (event) => {
    if (event.target === this.#dialog) this.#dialog.close('cancel');
  };

  #handleDialogClose = () => {
    const button = this.#pendingButton;
    this.#pendingButton = null;

    if (this.#dialog.returnValue !== 'confirm' || !button?.isConnected) {
      button?.focus();
      return;
    }

    this.#confirmedButton = button;
    button.click();
  };

  #trackRemoval(button) {
    const lineKey = button.closest('[data-line-key]')?.dataset.lineKey;
    if (!lineKey) return;

    const code = this.#cartItems().find(({ key }) => key === lineKey)?.properties?.[BUNDLE_DISCOUNT_PROPERTY];
    if (code) this.#pendingRemovals.set(lineKey, code);
  }

  #cartItems() {
    return window.kachingCartApi?.getCartData?.()?.items ?? [];
  }

  #scheduleSync() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#sync);
  }

  #sync = () => {
    const items = this.#cartItems();
    const parentKeys = new Set(
      items.filter(({ properties }) => properties?.[BUNDLE_PARENT_PROPERTY]).map(({ key }) => key)
    );
    const childKeys = new Set(
      items
        .filter(({ parent_relationship: relationship }) => parentKeys.has(relationship?.parent_key))
        .map(({ key }) => key)
    );

    this.#root.querySelectorAll('.kaching-cart-item[data-line-key]').forEach((line) => {
      const { lineKey } = line.dataset;
      line.toggleAttribute('data-bundle-parent', parentKeys.has(lineKey));
      line.toggleAttribute('data-bundle-child', childKeys.has(lineKey));
    });

    this.#releaseDiscounts(items);
  };

  async #releaseDiscounts(items) {
    const removed = [...this.#pendingRemovals].filter(([key]) => !items.some((item) => item.key === key));
    if (!removed.length) return;

    removed.forEach(([key]) => this.#pendingRemovals.delete(key));

    const codesInUse = new Set(items.map(({ properties }) => properties?.[BUNDLE_DISCOUNT_PROPERTY]?.toLowerCase()));
    const codesToRemove = new Set(
      removed.map(([, code]) => code.toLowerCase()).filter((code) => !codesInUse.has(code))
    );
    if (!codesToRemove.size) return;

    await this.#removeDiscountCodes(codesToRemove).catch(() => null);
  }

  async #removeDiscountCodes(codesToRemove) {
    const root = window.Shopify?.routes?.root ?? '/';
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((response) =>
      response.json()
    );
    const currentCodes = (cart.discount_codes ?? []).map(({ code }) => code);
    const remainingCodes = currentCodes.filter((code) => !codesToRemove.has(code.toLowerCase()));
    if (remainingCodes.length === currentCodes.length) return;

    await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: remainingCodes.join(',') }),
    });
    await window.kachingCartApi?.refreshCart?.();
  }
}

new KachingBundleLines().init();
